import React, { lazy, Suspense } from "react";
import SelfieBasedAttendanceImage2 from "@/assets/selfieAttendenceImages/waggex-verified-selfie-checkin-gps-tagged-india.webp?w=350;450&format=webp";
import SelfieBasedAttendanceImage4 from "@/assets/selfieAttendenceImages/waggex-face-recognition-attendance-features-proxy-touchless-ai.webp?w=350;400&format=webp";

import SelfieBasedAttendanceImage320 from "@/assets/selfieAttendenceImages/waggex-how-face-recognition-attendance-works-face-recognition320.webp";
import SelfieBasedAttendanceImage640 from "@/assets/selfieAttendenceImages/waggex-how-face-recognition-attendance-works-face-recognition640.webp?w=500&format=webp";

import { useNavigate } from "react-router-dom";
import mobile from "@/assets/selfieAttendenceImages/waggex-face-recognition-attendance-software-dashboard-india-mobile.webp?w=350;400&format=webp";

import StatsSection from "./common/StatsSection";
import { TfiLayoutLineSolid } from "react-icons/tfi";

const ThreeSteps = lazy(() => import("./ThreeSteps"));

const CustomerReviews = lazy(
  () => import("../components/payrolewebapp/CustomerReviews"),
);

const ComparisonTable = lazy(
  () => import("../components/payrolewebapp/ComparisonTable"),
);

const HomeFaq = lazy(() => import("../pages/HomeFaq"));

const AppDownloadCard = lazy(
  () => import("../components/common/AppDownloadCard"),
);

const PayrollBanner = lazy(() => import("../components/common/PayrollBanner"));

const ModernizeHR = lazy(() => import("../components/common/ModernizeHR"));

import { Helmet } from "react-helmet-async";
import { useHrmsCounts } from "../hooks/useHrmsCounts";
import { useInView } from "react-intersection-observer";

const selfieAttendanceFaqs1 = [
  {
    title:
      "Is a Face Recognition Attendance System as reliable as a biometric machine?",
    content:
      "Yes. A Face Recognition Attendance System can be highly reliable for everyday employee attendance. Traditional biometric machines verify an employee's identity at a fixed location using fingerprints or other biometric methods. Waggex uses AI-powered face recognition on the employee's smartphone and adds GPS verification. This means you can verify not only who marked attendance but also where the employee was when attendance was recorded.",
  },
  {
    title:
      "How does a Face Recognition Attendance System work on a smartphone?",
    content:
      "When an employee opens the Waggex Staff App to mark attendance, the front camera activates and the AI analyzes the employee's facial features in real time. The face is compared with the encrypted facial template created during enrollment. The process takes only a few seconds, while liveness detection helps verify that the attendance attempt comes from a real person rather than a photo or screen replay. No dedicated biometric machine is required.",
  },
  {
    title:
      "Can employees fake attendance on a Face Recognition Attendance System?",
    content:
      "Waggex uses face recognition and liveness detection to help prevent fraudulent attendance attempts. The system checks whether the captured face belongs to a live person rather than relying only on a static photograph. Attendance is also combined with GPS location and a timestamp, providing an additional verification layer. If an attendance record is created from an unexpected location, administrators can review it from the dashboard.",
  },
];

const selfieAttendanceFaqs2 = [
  {
    title: "How do employees log in to the Face Recognition Attendance App?",
    content:
      "Employees can access the Waggex Staff App using their registered email. To mark attendance, they complete a live face verification through the phone's front camera. There is no need to carry an attendance card or use a physical biometric machine for each attendance punch.",
  },
  {
    title:
      "Does a Face Recognition Attendance System work without internet connectivity?",
    content:
      "Depending on the app configuration and device capabilities, attendance information such as the selfie, GPS coordinates, and timestamp can be captured on the device and synchronized when connectivity becomes available. This can be useful for employees working in locations with intermittent internet connectivity.",
  },
  {
    title: "How is employee facial data stored and protected?",
    content:
      "During enrollment, Waggex can convert facial information into a numerical biometric template used for identity verification. The system is designed to use the template for subsequent face matching rather than relying on an employee photograph as the primary verification record. Facial templates and attendance information should be protected with encryption, secure transmission, and role-based access controls so that users can access only the information permitted by their role.",
  },
];

const rows = [
  {
    feature: "Hardware Cost",
    without: "₹8,000–₹40,000 per device",
    with: "Nil uses existing phones",
    manual: "Paper cost only",
    pf: "Auto-calculated",
  },
  {
    feature: "Proxy Prevention",
    without: "On-premises only",
    with: "AI face + GPS every punch",
    manual: "Not applicable",
    pf: "Payroll calculates",
  },
  {
    feature: "Remote & Field Staff",
    without: "Fixed entrance only",
    with: "Works from any location",
    manual: "Cannot verify",
    pf: <TfiLayoutLineSolid className="text-center ml-6 sm:ml-15" />,
  },
  {
    feature: "Payroll Auto-sync",
    without: "Requires manual export",
    with: "Real-time, no manual step",
    manual: "Fully manual",
    pf: <TfiLayoutLineSolid className="text-center ml-6 sm:ml-15" />,
  },
  {
    feature: "Maintenance",
    without: "Regular servicing needed",
    with: "Zero app updates are silent",
    manual: "None needed",
    pf: <TfiLayoutLineSolid className="text-center ml-6 sm:ml-15" />,
  },
];
const headers = [
  "FEATURES",
  "WAGGEX SELFIE",
  "BIOMATRIC MACHINE",
  "MANNUAL REGISTER",
  "PF / ESI / TDS",
];

const steps = [
  {
    number: "1",
    label: "OPEN APP",
    title: "Open the App",
    description:
      "Employees simply open Waggex on their existing Android or iOS device no biometric machine or company hardware required",
  },
  {
    number: "2",
    label: "TAKE SELFIE",
    title: "Take a Live Selfie",
    description:
      "A quick AI-verified selfie confirms employee identity in real time and prevents proxy or fake attendance",
  },
  {
    number: "3",
    label: "ATTENDANCE SYNCS",
    title: "Attendance Syncs Instantly",
    description:
      "Attendance records, GPS location, and work hours sync automatically to the admin dashboard and payroll system",
  },
];

const schema = [
  // SCHEMA 1: SoftwareApplication
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Waggex Face Recognition Attendance System",
    alternateName: "Waggex FaceLens",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Android, iOS",

    description:
      "Waggex is a face recognition attendance system for Indian businesses. Employees mark attendance using AI-powered face recognition on their smartphones with GPS verification, liveness detection, and automatic attendance and payroll synchronization.",

    url: "https://www.waggex.com/face-recognition-attendance-system",

    screenshot:
      "https://www.waggex.com/waggex-selfie-attendance-ai-face-recognition.webp",

    featureList: [
      "AI face recognition attendance",
      "GPS-tagged attendance",
      "No biometric hardware required",
      "Face recognition biometric attendance",
      "Liveness detection and anti-spoofing",
      "Employee attendance verification",
      "Real-time payroll synchronization",
      "Android and iOS support",
      "Field and remote employee attendance",
    ],

    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
      description: "Free trial available",
    },

    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      ratingCount: "312",
      bestRating: "5",
      worstRating: "1",
    },

    provider: {
      "@type": "Organization",
      name: "Waggex",
      url: "https://www.waggex.com/",
    },
  },

  // SCHEMA 2: FAQPage
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",

    mainEntity: [
      {
        "@type": "Question",
        name: "Is a Face Recognition Attendance System as reliable as a biometric machine?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. A Face Recognition Attendance System can be highly reliable for everyday employee attendance. Traditional biometric machines verify an employee's identity at a fixed location using fingerprints or other biometric methods. Waggex uses AI-powered face recognition on the employee's smartphone and adds GPS verification. This means you can verify not only who marked attendance but also where the employee was when attendance was recorded.",
        },
      },

      {
        "@type": "Question",
        name: "How does a Face Recognition Attendance System work on a smartphone?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "When an employee opens the Waggex Staff App to mark attendance, the front camera activates and AI analyzes the employee's facial features in real time. The face is compared with the facial template created during enrollment. Liveness detection helps verify that the attendance attempt comes from a real person rather than a photo or screen replay. No dedicated biometric machine is required.",
        },
      },

      {
        "@type": "Question",
        name: "Can employees fake attendance on a Face Recognition Attendance System?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Waggex uses face recognition and liveness detection to help prevent fraudulent attendance attempts. The system checks whether the captured face belongs to a live person rather than relying only on a static photograph. Attendance is also combined with GPS location and a timestamp, providing an additional verification layer. If an attendance record is created from an unexpected location, administrators can review it from the dashboard.",
        },
      },

      {
        "@type": "Question",
        name: "How do employees log in to the Face Recognition Attendance App?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Employees can access the Waggex Staff App using their registered email. To mark attendance, they complete a live face verification through the phone's front camera. There is no need to carry an attendance card or use a physical biometric machine for each attendance punch.",
        },
      },

      {
        "@type": "Question",
        name: "Does a Face Recognition Attendance System work without internet connectivity?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Depending on the app configuration and device capabilities, attendance information such as the selfie, GPS coordinates, and timestamp can be captured on the device and synchronized when connectivity becomes available. This can be useful for employees working in locations with intermittent internet connectivity.",
        },
      },

      {
        "@type": "Question",
        name: "How is employee facial data stored and protected?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "During enrollment, Waggex can convert facial information into a numerical biometric template used for identity verification. The system is designed to use the template for subsequent face matching rather than relying on an employee photograph as the primary verification record. Facial templates and attendance information should be protected with encryption, secure transmission, and role-based access controls so users can access only the information permitted by their role.",
        },
      },

      {
        "@type": "Question",
        name: "Can I replace my biometric attendance machine with a mobile app?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Waggex provides a mobile-based alternative to traditional biometric attendance machines. Employees can use smartphones with face recognition to mark attendance without requiring dedicated biometric hardware at every location. This can be particularly useful for businesses with multiple offices, branches, field employees, or remote teams.",
        },
      },

      {
        "@type": "Question",
        name: "Does the Face Recognition Attendance System work for field and remote employees?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Unlike a fixed biometric machine that is generally installed at one location, Waggex can allow employees to mark attendance from approved locations using their smartphones. GPS verification provides location information with the attendance record, making the system suitable for field employees, remote teams, warehouses, client sites, and multiple business locations.",
        },
      },

      {
        "@type": "Question",
        name: "How does the cost compare to a biometric attendance machine?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A mobile-based face recognition attendance system can reduce the need for dedicated biometric hardware, installation, and maintenance at each location. Waggex uses employees' smartphones for attendance verification, helping businesses avoid purchasing separate biometric machines for every office or work site. A free trial is available to evaluate the system before subscribing.",
        },
      },
    ],
  },

  // SCHEMA 3: Organization
  {
    "@context": "https://schema.org",
    "@type": "Organization",

    name: "Waggex",

    url: "https://www.waggex.com/",

    // LOGO PATH KEPT EXACTLY AS PROVIDED
    logo: "https://www.waggex.com/waggex-payroll-software-logo.png",

    description:
      "Waggex provides AI-powered face recognition attendance and complete HRMS solutions for Indian businesses, including GPS attendance tracking, payroll automation, employee management, and leave management.",

    areaServed: "IN",

    contactPoint: {
      "@type": "ContactPoint",
      contactType: "sales",
      areaServed: "IN",
      availableLanguage: ["English", "Hindi"],
    },
  },
];

export default function SelfieBasedAttendanceApp() {
  const navigate = useNavigate();
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { counts, loading } = useHrmsCounts(inView);
  const handleRedirect = () => {
    window.location.href = "https://app.waggex.com/signup";
  };

  return (
    <>
      <Helmet>
        <title>Face Recognition Attendance System for Indian Businesses</title>
        <meta
          name="description"
          content="Waggex Face Recognition Attendance System lets employees mark attendance via AI selfie on any smartphone, no hardware, no biometric machine. GPS-tagged, fraud-proof, and auto-synced to payroll"
        />
        <meta
          name="keywords"
          content="Face Recognition Attendance System,face recognition attendance app India,attendance without biometric machine,mobile attendance with GPS,employee attendance tracking app"
        />
        <link
          rel="canonical"
          href="https://www.waggex.com/face-recognition-attendance-system"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      </Helmet>
      <section className="w-full bg-[#d1dfea] overflow-hidden py-10 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-10">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              {/* Badge */}
              <div className="inline-block border border-[#10324F] rounded-xl px-5 py-2 text-[#10324F] text-xs sm:text-sm font-medium">
                Face Recognition Attendance System
              </div>

              {/* Heading */}
              <h1 className="mt-8 text-[#10324F] font-bold uppercase tracking-wide text-xl md:text-[28px] min-h-18 md:w-149.25">
                Face Recognition Attendance System
                <br /> No Hardware Needed
              </h1>

              {/* Description */}
              <p className="service-list-item-text py-4 max-w-md sm:max-w-2xl mx-auto">
                Waggex is a face recognition attendance system that runs
                entirely on the smartphones your employees already carry. No
                biometric machine to buy, no device to maintain, no fixed
                location required. Employees mark attendance with an AI-verified
                selfie app in under two seconds GPS-tagged, fraud-proof, and
                auto-synced to payroll
              </p>

              {/* Buttons */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  onClick={() =>
                    (window.location.href = "https://app.waggex.com/signup")
                  }
                  className="w-full sm:w-auto bg-[#16395f] hover:bg-[#0f2d4c] transition-all duration-300 text-white px-14 py-3 rounded-xl shadow-md text-sm sm:text-base font-medium"
                >
                  Start Free Trial
                </button>

                <button
                  onClick={() => (window.location.href = "/contact-us")}
                  className="w-full sm:w-auto border border-[#10324F] text-[#10324F] hover:bg-[#10324F] hover:text-white transition-all duration-300 px-14 py-3 rounded-xl text-sm sm:text-base font-medium"
                >
                  Book a Demo
                </button>
              </div>

              {/* Features */}
              <div className="mt-8 grid grid-cols-2 gap-3 text-[#10324F] text-xs sm:text-sm">
                {[
                  "No Biometric Hardware",
                  "Supports Ios & Android",
                  "Works 24/7 NonStop",
                  "Payroll Sync Automatically",
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#10324F] shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Image */}

            <div className="relative flex justify-center items-center">
              <picture>
                <source media="(max-width: 639px)" srcSet={mobile[0]} />
                <img
                  src={mobile[1]}
                  sizes="(max-width: 640px) 90vw"
                  width={400}
                  height={406}
                  fetchPriority="high"
                  loading="eager"
                  decoding="async"
                  alt="Employee face recognition"
                  className="object-contain"
                />
              </picture>
            </div>
          </div>
        </div>
      </section>
      <section ref={ref}>
        <StatsSection counts={counts} loading={loading} />
      </section>
      {/* Row 2 */}
      <section className="bg-[#FFFFFF] w-full py-10 px-6 min-h-120">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center ">
            <div className="order-2 md:order-1 flex justify-center h-80">
              <img
                src={SelfieBasedAttendanceImage2[0]}
                sizes="(max-width: 640px) 90vw"
                width="382"
                height="320"
                fetchPriority="high"
                decoding="async"
                alt="Waggex verified face recognition attendance replacing fingerprint scanner GPS-tagged, fraud-proof check-in for Indian businesses"
                className="object-contain block sm:hidden"
              />
              <img
                src={SelfieBasedAttendanceImage2[1]}
                sizes="(max-width: 640px) 90vw"
                width="410"
                height="320"
                fetchPriority="high"
                decoding="async"
                alt="Waggex verified face recognition attendance replacing fingerprint scanner GPS-tagged, fraud-proof check-in for Indian businesses"
                className="object-contain hidden sm:block"
              />
            </div>

            <div className="order-1 md:order-2 space-y-4">
              <div className="payroll-section-content-width">
                <h2 className="selfi-service-section-heading">
                  Smart Face Recognition Attendance, No Biometric Machine
                  Required
                </h2>

                <p className="selfie-service-list-item-text py-4">
                  Replace your biometric attendance machine with a face
                  recognition attendance system that works on any Android or iOS
                  device
                </p>

                <ul className="space-y-3 mt-3">
                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Real-Time Cloud Sync Across Locations
                    </span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Fast, Contactless & Employee-Friendly
                    </span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Accurate Attendance with AI Verification
                    </span>
                  </li>

                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      No Expensive Hardware or Maintenance
                    </span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Prevent Proxy & Buddy Punching
                    </span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Geo-Tagged & Time-Stamped Check-ins
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className=" bg-[#D1DFEA33] backdrop-blur-[30px] py-10 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            {/* LEFT — Feature List */}

            <div className="payroll-section-content-width">
              <h2 className="selfi-service-section-heading mb-3">
                Why Replace Your Biometric Machines With Face Recognition
              </h2>

              <div className="space-y-4">
                {/* Item 1 */}
                <div className="flex gap-4 items-start">
                  <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                    <div className="diamond-icon-sm"></div>
                  </div>
                  <div>
                    <h2 className="font-semibold text-[#10324F] text-sm sm:text-base">
                      Prevent Proxy & Buddy Punching
                    </h2>
                    <p className="selfie-service-list-item-text mt-1">
                      Every check-in is verified with real-time AI face
                      recognition, ensuring only the right employee marks
                      attendance
                    </p>
                  </div>
                </div>

                {/* Item 2 */}
                <div className="flex gap-4 items-start">
                  <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                    <div className="diamond-icon-sm"></div>
                  </div>
                  <div>
                    <h2 className="font-semibold text-[#10324F] text-sm sm:text-base">
                      Fast & Contactless Check-ins
                    </h2>
                    <p className="selfie-service-list-item-text mt-1">
                      Employees can mark attendance instantly using their
                      smartphone. No queues, no fingerprint scanners, no
                      physical contact
                    </p>
                  </div>
                </div>

                {/* Item 3 */}
                <div className="flex gap-4 items-start">
                  <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                    <div className="diamond-icon-sm"></div>
                  </div>
                  <div>
                    <h2 className="font-semibold text-[#10324F] text-sm sm:text-base">
                      Accurate AI Face Verification
                    </h2>
                    <p className="selfie-service-list-item-text mt-1">
                      Advanced facial recognition validates employee identity
                      within seconds for secure and reliable attendance records
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* RIGHT — Image */}

            <div className="flex justify-center md:justify-end">
              <img
                src={SelfieBasedAttendanceImage4[0]}
                sizes="(max-width: 640px) 100vw"
                width="382"
                height="272"
                fetchPriority="high"
                alt="Waggex face recognition attendance features prevent proxy attendance, touchless check-in, instant AI face verification India"
                className="object-contain hidden sm:block"
              />
              <img
                src={SelfieBasedAttendanceImage4[1]}
                sizes="(max-width: 640px) 100vw"
                width="400"
                height="286"
                fetchPriority="high"
                alt="Waggex face recognition attendance features prevent proxy attendance, touchless check-in, instant AI face verification India"
                className="object-contain block sm:hidden"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#FFFFFF] w-full py-10 px-6 min-h-110">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="order-2 md:order-1 flex justify-center">
              <div className="hidden sm:block">
                <img
                  src={SelfieBasedAttendanceImage640}
                  sizes="(max-width:640px) 100vw"
                  width="560"
                  height="312"
                  fetchPriority="high"
                  alt="How Waggex face recognition attendance works AI identifies employee, captures GPS, syncs to payroll instantly India"
                  className="
      object-contain
    "
                />
              </div>

              <div className="sm:hidden">
                <img
                  src={SelfieBasedAttendanceImage320}
                  sizes="(max-width:640px) 100vw"
                  width="320"
                  height="178"
                  fetchPriority="high"
                  alt="How Waggex face recognition attendance works AI identifies employee, captures GPS, syncs to payroll instantly India"
                  className=" object-contain"
                />
              </div>
            </div>
            <div className="order-1 md:order-2 space-y-4">
              <div className="payroll-section-content-width">
                <h2 className="selfi-service-section-heading">
                  How Face Recognition Attendance System Works
                </h2>

                <p className="selfie-service-list-item-text py-4">
                  The Waggex face recognition attendance system works in three
                  steps. Employees open the app, take a live AI-verified selfie,
                  and attendance is marked in seconds. Every check-in is
                  geo-tagged with GPS coordinates, time-stamped, and stored in
                  the cloud giving HR complete visibility with zero manual
                  effort
                </p>
                <h2 className="text-[#10324F] font-roboto font-normal">
                  The system instantly :
                </h2>
                <ul className="space-y-3 mt-3">
                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Verifies identity using AI face recognition
                    </span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Captures live GPS location & time stamps
                    </span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Prevents fake, proxy, and duplicate attendance
                    </span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Syncs attendance data in real time
                    </span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Generates accurate payroll & HR reports
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Suspense fallback={null}>
        <ThreeSteps
          title="Fast. Secure. Attendance in Seconds."
          subtitle="No biometric machines. No setup hassle. Employees can mark attendance instantly using any Android smartphone they already use."
          steps={steps}
        />
        <ComparisonTable
          headers={headers}
          rows={rows}
          title="Face Recognition Attendance System vs Biometric Machine"
          subtitle="A biometric machine at your door costs ₹8,000 to ₹40,000 per device, per location. Here is how that compares"
          source="selfie"
        />
        <CustomerReviews />
        <HomeFaq
          faq1={selfieAttendanceFaqs1}
          faq2={selfieAttendanceFaqs2}
          subtitle="Everything you need to know about Waggex payroll software in India."
        />

        <PayrollBanner
          heading="Already Have Attendance Sorted? Just Need HRMS Software?"
          description="Waggex runs payroll independently too. Calculate salaries, file PF, ESI and TDS, and generate payslips without the full HR suite"
        />
        <AppDownloadCard />
        <ModernizeHR
          heading=" No Biometric Machine. No Maintenance."
          description="Just Accurate Attendance.Join 10,000+ Indian businesses using Waggex face recognition attendance system to eliminate buddy punching, cut HR workload, and run payroll from verified data automatically"
        />
      </Suspense>
    </>
  );
}
